/**
 * Estilos de "Simular venda" e do "Cenário do PCP" (painéis laterais) e dos
 * botões de ação do topo. Mesmo desenho do resto da página: texto neutro,
 * UM sinal de cor por linha (o ponto do estado), nada de rolagem lateral.
 */
import styled from "styled-components";

import { BP_MOBILE } from "../../styles/canonical/breakpoints";

const celular = `(max-width: ${BP_MOBILE}px)`;
const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;

/* Botão do topo: ícone + rótulo; no celular só o ícone (o nome fica no aria-label). */
export const BotaoAcao = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.surfaceAlt};
    }
  }

  @media ${celular} {
    padding: 0 9px;

    .rotulo {
      display: none;
    }
  }
`;

/* Corpo do painel: seções empilhadas com respiro. */
export const CorpoPainel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
`;

export const Secao = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  h3 {
    margin: 0;
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textTertiary};
  }

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

/* Formulário do painel: campos em coluna no celular, lado a lado acima. */
export const Formulario = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  min-width: 0;

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1 1 160px;
    min-width: 0;
    font-size: var(--text-min, 12px);
    font-weight: 600;
    color: ${({ theme }) => theme.textSecondary};
  }

  input,
  select {
    min-height: 36px;
    width: 100%;
    max-width: 100%;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    /* 16px no celular: abaixo disso o iOS dá zoom ao focar. */
    font-size: 16px;
  }

  @media (min-width: ${BP_MOBILE + 1}px) {
    input,
    select {
      font-size: 13px;
    }
  }
`;

/* Lista de ambientes da venda com a classe de cada um (opcional). */
export const ListaClasses = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;

  li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    font-size: 13px;
    color: ${({ theme }) => theme.textPrimary};
  }

  li + li {
    border-top: 1px dashed ${({ theme }) => theme.border};
  }
`;

/* O veredito: uma frase grande, com o único ponto de cor do bloco. */
export const Veredito = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};

  .frase {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ theme }) => theme.textPrimary};
  }

  .frase i {
    flex: 0 0 auto;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $tom }) => tom($tom, "text")};
  }

  .entrada {
    font-size: 13px;
    color: ${({ theme }) => theme.textSecondary};
    font-variant-numeric: tabular-nums;
  }
`;

/* Linhas de texto com um ponto de estado à esquerda (motivos, conferência). */
export const ListaComPonto = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  li {
    display: grid;
    grid-template-columns: 7px minmax(0, 1fr);
    column-gap: 9px;
    align-items: baseline;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme.textPrimary};
  }

  li > i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    transform: translateY(-1px);
    background: ${({ theme }) => theme.border};
  }

  li > i[data-tom] {
    background: var(--neutral-text);
  }

  li > i[data-tom="success"] {
    background: var(--success-text);
  }

  li > i[data-tom="warning"] {
    background: var(--warning-text);
  }

  li > i[data-tom="danger"] {
    background: var(--danger-text);
  }

  .sub {
    display: block;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
  }
`;

/* Tabela sem <table>: rótulo à esquerda, valores alinhados à direita. */
export const Tabela = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${({ $colunas = "auto auto" }) => $colunas};
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  overflow: hidden;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  > span {
    min-width: 0;
    padding: 7px 12px;
    border-top: 1px solid ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > span.num {
    text-align: right;
  }

  > span.cab {
    border-top: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textTertiary};
  }

  > span.forte {
    font-weight: 650;
  }

  > span.fraco {
    color: ${({ theme }) => theme.textSecondary};
  }
`;

/* O que digitar: linha da planilha + a instrução em destaque. */
export const Instrucao = styled.li`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 0;
  min-width: 0;

  & + & {
    border-top: 1px dashed ${({ theme }) => theme.border};
  }

  .onde {
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .texto {
    font-size: 13.5px;
    font-weight: 650;
    line-height: 1.45;
    color: ${({ theme }) => theme.textPrimary};
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }

  .aviso {
    font-size: var(--text-min, 12px);
    line-height: 1.45;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const ListaInstrucoes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  min-width: 0;
`;

export const LinhaBotoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;
